import {
  Button,
  ConfirmDialog,
  Dialog,
  DialogActions,
  DialogContent,
  MultiSelectChipInput,
  useSnackbarContext,
} from "@ameelio/ui";
import { useMutation } from "@apollo/client/react";
import { Stack } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AmeelioStaff, AmeelioStaffEntitlement } from "../api/graphql.ts";
import useApolloErrorHandler from "../lib/handleApolloError.ts";
import { useCurrentUser } from "../SessionBoundary.tsx";
import { DeactivateAmeelioStaffDocument } from "./DeactivateAmeelioStaff.generated.ts";
import { GetAmeelioStaffListDocument } from "./GetAmeelioStaffList.generated.ts";
import { UpdateAmeelioStaffDocument } from "./UpdateAmeelioStaff.generated.ts";

type Props = {
  onClose: () => void;
  staff: Pick<
    AmeelioStaff,
    "id" | "fullName" | "entitlements" | "learnRoles" | "active"
  >;
};

const LearnRoles = ["AccountAdmin", "TeacherEnrollment"] as const;
type LearnRole = (typeof LearnRoles)[number];

type FormData = {
  entitlements?: AmeelioStaffEntitlement[];
  learnRoles?: LearnRole[];
};

export default function EditAmeelioStaffModal({ onClose, staff }: Props) {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const snackbarContext = useSnackbarContext();
  const handleApolloError = useApolloErrorHandler();

  const [updateStaff] = useMutation(UpdateAmeelioStaffDocument, {
    onError: handleApolloError,
    onCompleted: () => {
      snackbarContext.alert("success", t("Staff updated"));
      onClose();
    },
    refetchQueries: [{ query: GetAmeelioStaffListDocument }],
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    control,
  } = useForm<FormData>({
    mode: "onTouched",
    defaultValues: {
      entitlements: staff?.entitlements || [],
      learnRoles: (staff?.learnRoles as LearnRole[]) || [],
    },
  });

  const [deactivateStaff] = useMutation(DeactivateAmeelioStaffDocument, {
    onError: handleApolloError,
    onCompleted: () => {
      snackbarContext.alert("success", t("Staff deactivated"));
      onClose();
    },
  });

  async function onDeactivate() {
    if (staff) {
      await deactivateStaff({
        variables: {
          input: {
            id: staff.id,
          },
        },
      });
    }
    setDeactivating(false);
    onClose();
  }

  const [deactivating, setDeactivating] = useState<boolean>(false);

  const submitForm = handleSubmit(async (values) => {
    await updateStaff({
      variables: {
        input: {
          id: staff.id,
          ...values,
          entitlements: values.entitlements || [],
        },
      },
    });
  });

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      title={
        staff
          ? t("Editing {{name}}", {
              name: staff.fullName,
            })
          : t("Add New Staff")
      }
    >
      <DialogContent>
        {deactivating && (
          <ConfirmDialog
            title={t("Deactivate staff")}
            description={t("Are you sure?", {
              name: staff?.fullName || t("this staff member"),
            })}
            variant="remove"
            onCancel={() => setDeactivating(false)}
            onConfirm={onDeactivate}
            confirmText={t("Confirm deactivation")}
          />
        )}
        <form onSubmit={submitForm}>
          <Stack spacing={2}>
            <MultiSelectChipInput
              control={control}
              name="entitlements"
              label={t("Entitlements")}
              items={Object.values(AmeelioStaffEntitlement).map(
                (enumValue: AmeelioStaffEntitlement) => ({
                  name: enumValue,
                  value: enumValue,
                })
              )}
              disabled={staff?.id === user.id}
            />
            <MultiSelectChipInput
              control={control}
              name="learnRoles"
              label={t("Learn roles")}
              items={LearnRoles.map((str) => ({
                name: str,
                value: str,
              }))}
            />
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        {staff && staff.active && (
          <Button
            color="error"
            variant="outlined"
            disabled={isSubmitting}
            key="delete"
            onClick={() => setDeactivating(true)}
            sx={{ mr: 1 }}
          >
            {t("Deactivate")}
          </Button>
        )}
        <Button variant="outlined" key="back" onClick={onClose} sx={{ mr: 1 }}>
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          key="submit"
          type="submit"
          onClick={submitForm}
          disabled={isSubmitting || !isValid}
        >
          {t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
